import { useEffect, useState } from "react";

import { CheckCircle2, Circle, Loader2 } from "lucide-react";

import { cn } from "../../../lib/utils";

type StepStatus = "pending" | "active" | "complete";

type ProgressStep = {
  id: string;
  label: string;
  status: StepStatus;
};

type AddPropertyMailingSearchProgressProps = {
  isLoading: boolean;
  isComplete?: boolean;
  noMatchesFound?: boolean;
  variant?: "modal" | "inline";
  className?: string;
};

const STEPS = [
  { id: "id", label: "Checking your ID on file" },
  { id: "county", label: "Searching county property records" },
  { id: "match", label: "Matching your mailing address" },
];

const useStepProgress = (isLoading: boolean, isComplete: boolean) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      if (isComplete) {
        setActiveIndex(STEPS.length);
      }
      return undefined;
    }

    setActiveIndex(0);
    const interval = window.setInterval(() => {
      setActiveIndex((current) => {
        if (current >= STEPS.length - 1) {
          return current;
        }
        return current + 1;
      });
    }, 1800);

    return () => window.clearInterval(interval);
  }, [isLoading, isComplete]);

  const steps: ProgressStep[] = STEPS.map((step, index) => {
    if (isComplete || index < activeIndex) {
      return { ...step, status: "complete" };
    }
    if (index === activeIndex && isLoading) {
      return { ...step, status: "active" };
    }
    return { ...step, status: "pending" };
  });

  return steps;
};

const StepIcon = ({ status }: { status: StepStatus }) => {
  if (status === "complete") {
    return <CheckCircle2 className="w-5 h-5 text-secondary shrink-0" />;
  }
  if (status === "active") {
    return <Loader2 className="w-5 h-5 text-secondary animate-spin shrink-0" />;
  }
  return <Circle className="w-5 h-5 text-muted-foreground/40 shrink-0" />;
};

export const AddPropertyMailingSearchProgress = ({
  isLoading,
  isComplete = false,
  noMatchesFound = false,
  variant = "inline",
  className = "",
}: AddPropertyMailingSearchProgressProps) => {
  const steps = useStepProgress(isLoading, isComplete);

  const title = isLoading
    ? "Searching for properties"
    : noMatchesFound
      ? "Search complete"
      : "Properties found";

  const description = isLoading
    ? "We are looking for additional properties in your county that match the mailing address on your government ID."
    : noMatchesFound
      ? "We searched the county for any additional properties that match your mailing address on file and couldn't find one. Search below to add a property by address or parcel."
      : "We found properties that match your mailing address.";

  return (
    <div
      className={cn(
        variant === "modal" ? "text-center" : "rounded-xl border border-border bg-card p-4",
        className,
      )}
    >
      <h3
        className={cn(
          "font-medium text-foreground",
          variant === "modal" ? "text-lg mb-1" : "text-sm mb-3",
        )}
      >
        {title}
      </h3>
      <p
        className={cn(
          "text-muted-foreground",
          variant === "modal" ? "text-sm mb-6" : "text-xs mb-4",
        )}
      >
        {description}
      </p>

      <div className="flex flex-col items-start gap-0 max-w-sm mx-auto text-left">
        {steps.map((step, index) => (
          <div key={step.id} className="flex gap-3 w-full">
            <div className="flex flex-col items-center">
              <StepIcon status={step.status} />
              {index < steps.length - 1 ? (
                <div
                  className={cn(
                    "w-0.5 flex-1 min-h-[28px] my-1 rounded-full transition-colors duration-500",
                    step.status === "complete"
                      ? "bg-secondary"
                      : "bg-muted-foreground/20",
                  )}
                />
              ) : null}
            </div>
            <p
              className={cn(
                "text-sm pb-5",
                step.status === "active"
                  ? "text-foreground font-medium"
                  : step.status === "complete"
                    ? "text-foreground"
                    : "text-muted-foreground",
              )}
            >
              {step.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AddPropertyMailingSearchProgress;
